import React, { useEffect, useContext, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
    IAddress,
    LeaseAgreementStatus,
    LeaseAgreementSource,
    ILeaseAgreement,
    ITenantLeaseInvite,
    IdLeaseAgreement,
} from 'realhaus-sdk';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { KeyboardArrowLeft } from '@material-ui/icons';
import { RightDrawer } from '../uiComponents/rightDrawer';
import { RootStoreContext } from '../../global/storeContext';
import { streetAddress } from '../../utils/address';
import { TabComponent, TabSection } from '../uiComponents/tabs';
import { LeaseTermsComponent } from './leaseTerms';
import { LeaseRentDepositFeesComponent } from './rentDepositFees';
import { LeasePSIPolicyComponent } from './petsSmokingInsurance';
import { LeaseClausesComponent } from './clauses';
import { LeaseRulesComponent } from './rules';
import { GridContent } from '../uiComponents/UIComponents';
import { LeaseAgreementReadView } from './leaseAgreementReadView';
import { RequestTenantLeaseSignatureDialog } from './dialog';
import { LeaseTenantInformationComponent } from './tenantInformation';
import { LeaseDocumentsComponent } from './documents';
import { RequestedChanges } from './requestedChanges';
import { ConfirmDialog } from '../uiComponents/confirmationDialog';
import { debounce } from 'lodash';
import { useIdentityGate } from '../../hooks/useIdentityGate';
import { CompleteAccountSetupDialog } from '../rentApplication/dialogs/completeAccountSetupDialog';
import { usePaymentAmountLimit } from '../../hooks/usePaymentAmountLimit';
import {
    getTenantInviteError,
    isInvitePending,
    isSameTenantInvite,
    normalizeTenantInvite,
} from '../../utils/leaseInvite';

export interface LeaseAgreementProps {
    leaseAgreementId?: string;
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    onLeaseUpdated: (lease: IdLeaseAgreement, tenantInvite?: ITenantLeaseInvite) => void;
}

export enum ViewMode {
    MODIFY,
    PREVIEW,
}
type IDTenantLeaseInvite = { id: string } & ITenantLeaseInvite;
export const LeaseAgreementLandlordComponent: React.FC<LeaseAgreementProps> = observer(
    ({ leaseAgreementId, isOpen, onOpen, onClose, onLeaseUpdated }) => {
        const { leaseStore, uiStore } = useContext(RootStoreContext);
        const { checkAndProceed, gateDialogProps } = useIdentityGate();
        const { exceedsLimit, limitMessage } = usePaymentAmountLimit();
        const [leaseAgreement, setLeaseAgreement] = useState<ILeaseAgreement>();
        const [leaseModified, setLeaseModified] = useState(false);
        const leaseEditVersion = React.useRef(0);
        // The tenant invite as last loaded from / written to the database
        const savedInviteRef = React.useRef<ReturnType<typeof normalizeTenantInvite>>();
        const isReviewingRef = React.useRef(false);
        const [tenant, setTenant] = useState<{ firstname: string; lastname: string }>();
        const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.MODIFY);
        const [tenantLeaseInvite, setTenantLeaseInvite] = React.useState<
            { id: string } & ITenantLeaseInvite
        >({} as IDTenantLeaseInvite);
        const [isReadOnly, setIsReadOnly] = React.useState(true);
        // Async handlers (e.g. after the identity gate) must see the latest edits, not what was
        // rendered when they started.
        const latestRef = React.useRef({
            leaseAgreement,
            leaseModified,
            tenantLeaseInvite,
            isReadOnly,
        });
        latestRef.current = { leaseAgreement, leaseModified, tenantLeaseInvite, isReadOnly };

        const handleClose = () => {
            setViewMode(ViewMode.MODIFY);
            onClose();
        };

        const init = () => {
            // get lease agreement
            if (leaseAgreementId) {
                // stay locked until the lease and its tenant invite are fully loaded
                setIsReadOnly(true);
                savedInviteRef.current = undefined;
                leaseStore.getLeaseAgreementById(leaseAgreementId).then(async (l) => {
                    if (
                        !l ||
                        (l.source !== LeaseAgreementSource.NON_REALHAUS &&
                            (!l.tenantIds || l.tenantIds.length === 0))
                    ) {
                        closeView();
                        return;
                    }
                    setLeaseAgreement(l);
                    setLeaseModified(false);

                    // get tenant
                    if (l.tenantIds.length > 0) {
                        const tenantId = l.tenantIds[0];

                        const tInfo = await leaseStore.getTenantInfo(tenantId);
                        if (!tInfo) {
                            throw new Error(
                                `Could not retrieve tenant Info for tenantId: ${tenantId}`,
                            );
                        }
                        setTenant({ firstname: tInfo.firstname, lastname: tInfo.lastname });
                    } else if (l.source === LeaseAgreementSource.NON_REALHAUS) {
                        // get lease invite info
                        const tInfo = await leaseStore.getTenantInvitationInfo(l.id);
                        if (tInfo) {
                            setTenant({ firstname: tInfo.firstname, lastname: tInfo.lastname });
                            setTenantLeaseInvite(tInfo);
                            savedInviteRef.current = normalizeTenantInvite(tInfo);
                        } else {
                            console.warn(
                                `Could not retrieve tenant Invitation Info for Lease: ${l.id}`,
                            );
                        }
                    }

                    // Lease should be editable only if agreement is still in draft
                    setIsReadOnly(l.status !== LeaseAgreementStatus.DRAFT);
                });
            } else {
                console.log('no lisiting');
                closeView();
                return;
            }
        };

        useEffect(() => {
            init();
        }, [leaseAgreementId]);

        const debounceSave = useMemo(() => {
            return debounce(async (updatedLease: ILeaseAgreement, editVersion: number) => {
                if (!updatedLease || !leaseAgreementId) return;

                try {
                    await leaseStore.updateLeaseAgreement(leaseAgreementId, updatedLease);
                    if (leaseEditVersion.current === editVersion) {
                        setLeaseModified(false);
                    }
                } catch (err) {
                    // the lease stays marked as modified so the next edit or REVIEW saves it again
                    uiStore.error('Failed to autosave lease agreement');
                }
            }, 2500);
        }, [leaseAgreementId, leaseStore, uiStore]);

        useEffect(() => {
            if (leaseModified && !!leaseAgreement) {
                debounceSave({ ...leaseAgreement }, leaseEditVersion.current);
            }
        }, [debounceSave, leaseAgreement, leaseModified]);

        // debounceSave is recreated when the lease changes (including the drawer closing), so this
        // saves a pending edit instead of dropping it when the landlord leaves within the debounce window.
        useEffect(() => {
            return () => {
                debounceSave.flush();
            };
        }, [debounceSave]);

        const closeView = () => {
            leaseEditVersion.current += 1;
            setLeaseAgreement(undefined);
            setLeaseModified(false);
            setTenant(undefined);
            setTenantLeaseInvite({} as IDTenantLeaseInvite);
            savedInviteRef.current = undefined;
            setIsReadOnly(true);
            handleClose();
        };

        const handleClosePreview = () => {
            setViewMode(ViewMode.MODIFY);
        };

        const updateLeaseAgreementProperty = (field: keyof ILeaseAgreement) => (value: unknown) => {
            if (!leaseAgreement || isReadOnly) return;

            leaseEditVersion.current += 1;
            setLeaseModified(true);
            setLeaseAgreement({
                ...leaseAgreement,
                [field]: value,
            });
        };

        const updateTenantLeaseInvite =
            (prop: keyof ITenantLeaseInvite) => (event: React.ChangeEvent<{ value: unknown }>) => {
                setTenantLeaseInvite((tenantLeaseInvite) => ({
                    ...tenantLeaseInvite,
                    [prop]: event.target.value,
                }));
            };

        const getReviewError = (state: typeof latestRef.current) => {
            const { leaseAgreement: lease, tenantLeaseInvite: invite, isReadOnly: readOnly } = state;

            if (!readOnly && isInvitePending(lease)) {
                const inviteError = getTenantInviteError(invite);
                if (inviteError) return inviteError;
            }

            if (exceedsLimit(lease?.rentAmount) || exceedsLimit(lease?.securityDepositAmount)) {
                return limitMessage;
            }
            return undefined;
        };

        const handleSavePreviewLeaseAgreement = () => {
            const reviewError = getReviewError(latestRef.current);
            if (reviewError) {
                uiStore.error(reviewError);
                return;
            }
            checkAndProceed(async () => {
                // the landlord may have kept editing while the account setup check was running
                const latest = latestRef.current;
                const lease = latest.leaseAgreement;
                if (!leaseAgreementId || !lease || isReviewingRef.current) return;

                const latestError = getReviewError(latest);
                if (latestError) {
                    uiStore.error(latestError);
                    return;
                }

                const invitePayload = normalizeTenantInvite(latest.tenantLeaseInvite);
                const shouldSaveLease = !latest.isReadOnly && latest.leaseModified;
                const shouldSaveInvite =
                    !latest.isReadOnly &&
                    isInvitePending(lease) &&
                    !isSameTenantInvite(invitePayload, savedInviteRef.current);
                const needsWrite = shouldSaveLease || shouldSaveInvite;

                isReviewingRef.current = true;
                if (needsWrite) uiStore.showLoading();
                try {
                    if (shouldSaveLease) {
                        const saveVersion = leaseEditVersion.current;
                        debounceSave.cancel();
                        //ToDo: check that tenant has not signed lease before saving modified lease agreement
                        await leaseStore.updateLeaseAgreement(leaseAgreementId, lease);
                        // an edit made while saving keeps the lease modified so autosave picks it up
                        if (leaseEditVersion.current === saveVersion) {
                            setLeaseModified(false);
                        }
                    }

                    if (shouldSaveInvite) {
                        const inviteId = await leaseStore.saveTenantLeaseInvite(
                            leaseAgreementId,
                            invitePayload,
                            latest.tenantLeaseInvite.id,
                        );
                        savedInviteRef.current = invitePayload;
                        setTenantLeaseInvite((currentInvite) => ({
                            ...currentInvite,
                            ...invitePayload,
                            id: inviteId,
                        }));
                        setTenant({
                            firstname: invitePayload.firstname,
                            lastname: invitePayload.lastname,
                        });
                    }

                    if (needsWrite) uiStore.success('Lease Agreement updated');
                    // show lease preview
                    setViewMode(ViewMode.PREVIEW);
                } catch (err) {
                    uiStore.error('Failed to update lease agreement');
                } finally {
                    if (needsWrite) uiStore.hideLoading();
                    isReviewingRef.current = false;
                }
            });
        };

        const handleTenantSignatureRequest = () =>
            checkAndProceed(async () => {
                if (
                    !leaseAgreementId ||
                    !leaseAgreement ||
                    (leaseAgreement.source !== LeaseAgreementSource.NON_REALHAUS &&
                        (!leaseAgreement?.tenantIds || leaseAgreement?.tenantIds.length === 0))
                ) {
                    const err =
                        'Tenant signature request failed. Unable to find the tenant for this Lease Agreement!';
                    uiStore.error(err);
                    return;
                }

                uiStore.showLoading();
                try {
                    const updatedLeaseAgreement = {
                        ...leaseAgreement,
                        signatureRequestDate: Date.now(),
                        status: LeaseAgreementStatus.PENDING_TENANT_SIGNATURE,
                    };
                    await leaseStore.updateLeaseAgreement(leaseAgreementId, updatedLeaseAgreement);
                    setLeaseAgreement(updatedLeaseAgreement);
                    onLeaseUpdated(
                        {
                            id: leaseAgreementId,
                            ...updatedLeaseAgreement,
                        },
                        normalizeTenantInvite(latestRef.current.tenantLeaseInvite),
                    );
                    uiStore.success('Lease agreement sent to tenant');
                } catch (err) {
                    uiStore.error('Failed to send lease agreement to tenant');
                } finally {
                    uiStore.hideLoading();
                    handleClose();
                }
            });

        return (
            <>
                <RightDrawer isOpen={isOpen} onOpen={onOpen} onClose={handleClose}>
                    <Grid container direction='column' spacing={3}>
                        <Grid item>
                            {!!leaseAgreement && !!leaseAgreementId && (
                                <>
                                    <Box mb={2}>
                                        <LeaseAgreementHeader
                                            tenant={tenant}
                                            listingAddress={leaseAgreement.listingInfo.address}
                                            lease={{
                                                id: leaseAgreementId,
                                                ...leaseAgreement,
                                            }}
                                            refetchData={init}
                                        />
                                    </Box>
                                    <>
                                        {viewMode === ViewMode.MODIFY ? (
                                            <LeaseModifyComponent
                                                isReadOnly={isReadOnly}
                                                leaseAgreement={leaseAgreement}
                                                updateLeaseAgreementProperty={
                                                    updateLeaseAgreementProperty
                                                }
                                                handleReview={handleSavePreviewLeaseAgreement}
                                                updateTenantLeaseInvite={updateTenantLeaseInvite}
                                                tenantLeaseInvite={tenantLeaseInvite}
                                                leaseId={leaseAgreementId}
                                            />
                                        ) : (
                                            <LeasePreviewComponent
                                                leaseAgreement={leaseAgreement}
                                                handleBack={handleClosePreview}
                                                handleRequestSignature={
                                                    handleTenantSignatureRequest
                                                }
                                            />
                                        )}
                                    </>
                                </>
                            )}
                        </Grid>
                        <Grid item></Grid>
                    </Grid>
                </RightDrawer>
                <CompleteAccountSetupDialog {...gateDialogProps} />
            </>
        );
    },
);

const LeaseAgreementHeader: React.FC<{
    tenant: { firstname: string; lastname: string } | undefined;
    listingAddress: IAddress;
    lease: IdLeaseAgreement;
    refetchData: () => void;
}> = observer(({ tenant, listingAddress, lease, refetchData }) => {
    const { uiStore, leaseStore } = useContext(RootStoreContext);
    const [openConfirmCancel, setOpenConfirmCancel] = React.useState<boolean>(false);
    const isLandlord = uiStore.isLandlordMode();

    const handleOpenCancelDialog = () => {
        setOpenConfirmCancel(true);
    };

    const handleCloseCancelDialog = () => {
        setOpenConfirmCancel(false);
    };

    const handleCancelLease = async () => {
        try {
            uiStore.showLoading();
            await leaseStore.endLeaseAgreement(lease).then(() => {
                uiStore.success('Lease agreement cancelled');
                refetchData();
            });
        } catch (err) {
            uiStore.error('Could not cancel agreement at this moment.Please try again later');
        } finally {
            uiStore.hideLoading();
            handleCloseCancelDialog();
        }
    };

    return (
        <>
            <Grid container>
                <Grid item xs={6}>
                    {!!tenant ? (
                        <>
                            <Typography variant='body1'>Lease agreement for</Typography>
                            <Typography variant='h5'>
                                {tenant.firstname} {tenant.lastname}
                            </Typography>
                        </>
                    ) : (
                        <Typography variant='body1'>Non Realhaus Lease</Typography>
                    )}
                </Grid>
                <Grid
                    container
                    direction='column'
                    item
                    xs={6}
                    justifyContent='center'
                    alignItems='flex-end'
                    spacing={3}
                >
                    <Box marginBottom={2}>
                        <Typography variant='h6' align='right'>
                            {streetAddress(listingAddress)}
                            <br />
                            {listingAddress.city}, {listingAddress.province}
                        </Typography>
                    </Box>

                    {isLandlord && lease.status === LeaseAgreementStatus.DRAFT && (
                        <Box marginBottom={2}>
                            <Button
                                variant='outlined'
                                color='secondary'
                                onClick={handleOpenCancelDialog}
                            >
                                Cancel
                            </Button>
                        </Box>
                    )}
                    {lease.status === LeaseAgreementStatus.CANCELLED && (
                        <Box marginBottom={2}>
                            <Typography variant='subtitle1' color={'secondary'} gutterBottom>
                                This lease agreement is cancelled
                            </Typography>
                        </Box>
                    )}
                </Grid>
            </Grid>

            <ConfirmDialog
                title={'Cancel Lease Agreement'}
                content={
                    <>
                        <Typography>
                            Are you sure you want to cancel this agreement? This action cannot be
                            undone.
                        </Typography>
                    </>
                }
                openDialog={openConfirmCancel}
                closeDialog={(confirm: boolean) => () => {
                    if (confirm) {
                        handleCancelLease();
                    }
                    handleCloseCancelDialog();
                }}
            />
        </>
    );
});

interface ILeaseModifyComponentProps {
    isReadOnly: boolean;
    leaseAgreement: ILeaseAgreement;
    updateLeaseAgreementProperty: (field: keyof ILeaseAgreement) => (value: unknown) => void;
    handleReview: () => void;
    tenantLeaseInvite: ITenantLeaseInvite;
    leaseId: string;
    updateTenantLeaseInvite: (
        prop: keyof ITenantLeaseInvite,
    ) => (event: React.ChangeEvent<{ value: unknown }>) => void;
}

export const LeaseModifyComponent: React.FC<ILeaseModifyComponentProps> = observer(
    ({
        isReadOnly,
        leaseAgreement,
        updateLeaseAgreementProperty,
        handleReview,
        tenantLeaseInvite,
        updateTenantLeaseInvite,
        leaseId,
    }) => {
        // TODO: ensure sections do no allow edit for agreements not in draft status
        const isNonRealhausLease = leaseAgreement.source === LeaseAgreementSource.NON_REALHAUS;
        const sections: TabSection[] = [
            {
                key: 'tenant-information',
                title: 'Tenant Information',
                subTitle: "Verify the Tenant's Information",
                hide: !isInvitePending(leaseAgreement),
                component: !!leaseAgreement ? (
                    <LeaseTenantInformationComponent
                        updateTenantLeaseInvite={updateTenantLeaseInvite}
                        tenantLeaseInvite={tenantLeaseInvite}
                        readOnly={isReadOnly}
                    />
                ) : (
                    <></>
                ),
            },
            {
                key: 'lease-terms-specifics',
                title: 'Lease Term & Specifics',
                subTitle: 'Verify the address and lease terms',
                component: !!leaseAgreement ? (
                    <LeaseTermsComponent
                        leaseAgreement={leaseAgreement}
                        setLeaseAgreement={updateLeaseAgreementProperty}
                        readOnly={isReadOnly}
                    />
                ) : (
                    <></>
                ),
            },
            {
                key: 'rent-deposit-fees',
                title: 'Rent Deposit & Fees',
                subTitle: 'Confirm rent amount, security deposits and any fees',
                component: !!leaseAgreement ? (
                    <LeaseRentDepositFeesComponent
                        leaseAgreement={leaseAgreement}
                        setLeaseAgreement={updateLeaseAgreementProperty}
                        readOnly={isReadOnly}
                    />
                ) : (
                    <></>
                ),
            },
            {
                key: 'pets-smoking-insurance',
                title: 'Pets, Smoking & Insurance',
                subTitle:
                    'Specify policies for pets, smoking, insurance & auto-rent payment requirements',
                component: !!leaseAgreement ? (
                    <LeasePSIPolicyComponent
                        leaseAgreement={leaseAgreement}
                        setLeaseAgreement={updateLeaseAgreementProperty}
                        readOnly={isReadOnly}
                    />
                ) : (
                    <></>
                ),
            },
            {
                key: 'lease-clauses',
                title: 'Clauses',
                subTitle: 'Specify clauses for the rent term',
                hide: isNonRealhausLease,
                component: !!leaseAgreement ? (
                    <LeaseClausesComponent
                        leaseAgreement={leaseAgreement}
                        updateProperty={updateLeaseAgreementProperty}
                        readOnly={isReadOnly}
                    />
                ) : (
                    <></>
                ),
            },
            {
                key: 'lease-rules',
                title: 'Rules',
                subTitle: 'Specify rules for the rent term',
                hide: isNonRealhausLease,
                component: !!leaseAgreement ? (
                    <LeaseRulesComponent
                        leaseAgreement={leaseAgreement}
                        updateProperty={updateLeaseAgreementProperty}
                        readOnly={isReadOnly}
                    />
                ) : (
                    <></>
                ),
            },
            {
                key: 'lease-documents',
                title: 'Lease Documents',
                subTitle: 'Upload other supporting documents',
                component: !!leaseAgreement ? (
                    <LeaseDocumentsComponent leaseId={leaseId} readOnly={isReadOnly} />
                ) : (
                    <></>
                ),
            },
            {
                key: 'Requested-changes-on-lease-documents',
                title: 'Requested Changes',
                subTitle: 'See changes requested on lease documents',
                component: !!leaseAgreement ? (
                    <RequestedChanges leaseAgreement={leaseAgreement} />
                ) : (
                    <></>
                ),
            },
        ];

        return (
            <TabComponent
                sections={sections}
                direction='vertical'
                onSubmit={handleReview}
                saveLabel={'REVIEW...'}
            />
        );
    },
);

interface ILeasePreviewComponentProps {
    leaseAgreement: ILeaseAgreement;
    handleBack: () => void;
    handleRequestSignature: () => void;
}
export const LeasePreviewComponent: React.FC<ILeasePreviewComponentProps> = observer(
    ({ leaseAgreement, handleBack, handleRequestSignature }) => {
        const [signatureRequestDialogOpen, setSignatureRequestDialogOpen] = React.useState(false);
        const isNonRealhausLease = leaseAgreement.source === LeaseAgreementSource.NON_REALHAUS;

        const openSignatureRequestDialog = () => {
            setSignatureRequestDialogOpen(true);
        };

        const closeSignatureRequestDialog = () => {
            setSignatureRequestDialogOpen(false);
        };

        const agreementStatusMessage = () => {
            switch (leaseAgreement.status) {
                case LeaseAgreementStatus.DRAFT: {
                    return 'This lease agreement is in progress.';
                }
                case LeaseAgreementStatus.PENDING_TENANT_SIGNATURE: {
                    return 'Waiting for tenant to sign.';
                }
                case LeaseAgreementStatus.SIGNED: {
                    return 'This lease agreement has been signed.';
                }
                default: {
                    return '';
                }
            }
        };

        return (
            <>
                <GridContent spacing={2}>
                    <Grid
                        item
                        justifyContent='space-between'
                        style={{ width: '100%', display: 'flex' }}
                    >
                        <Button onClick={handleBack} style={{ marginRight: '5px' }}>
                            <KeyboardArrowLeft />
                            BACK
                        </Button>
                        {leaseAgreement.status === LeaseAgreementStatus.DRAFT ? (
                            <Button
                                variant='contained'
                                color='primary'
                                style={{ borderRadius: '0' }}
                                onClick={openSignatureRequestDialog}
                            >
                                Request Signature
                            </Button>
                        ) : (
                            <Typography variant='subtitle1' color='primary'>
                                {agreementStatusMessage()}
                            </Typography>
                        )}
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={12}
                        style={{
                            height: '75vh',
                            width: '100%',
                            overflow: 'auto',
                            border: '1px solid #e8e8e8',
                        }}
                    >
                        <LeaseAgreementReadView leaseAgreement={leaseAgreement} />
                    </Grid>
                </GridContent>

                {/* Confirm send request for tenant signature */}
                <RequestTenantLeaseSignatureDialog
                    dialogOpen={signatureRequestDialogOpen}
                    closeDialog={closeSignatureRequestDialog}
                    onAccept={() => {
                        closeSignatureRequestDialog();
                        handleRequestSignature();
                    }}
                />
            </>
        );
    },
);
